import { bundleEventName } from 'const/pusherChannelEventConsts';
import dayjs, { Dayjs } from 'dayjs';
import { createGroupColors } from 'pages/oldPlanner/planner-map/map/PlannerMapMapUtils';
import {
  createFeatureStructure,
  PlannerBundle,
  PlannerOrder,
  uniqueWindowGroups,
  updatePlannerOrders,
  WindowGroups,
} from 'pages/planner/utils/usePlannerData';
import { useSubscribeToPlannerData } from 'pages/planner/utils/useSubscribeToPlannerData';
import { useCallback, useEffect, useRef, useState } from 'react';
import { settings } from 'settings';
import {
  BundleJson,
  BundlesPageJson,
  BundleStatusActive,
} from 'types/bundleTypes';
import { Order, Region, ProductType } from 'types/orderTypes';
import fetch from '../utils/fetch';

export interface PlannerData {
  loading: boolean;
  refetching: boolean;
  done: boolean;
  error: string;
  fetchedBundles: BundleJson[];
  fetchedOrders: PlannerOrder[];
  windowGroups: WindowGroups;
  ordersFeature: PlannerOrder[][];
  bundleFeature: PlannerBundle[];
  multiMarkers: PlannerOrder[][];
  reFetch: () => void;
  update: (orderIds: string[], event: string) => void;
}

export const usePlannerData = (region: Region, date: Dayjs) => {
  const [loading, setLoading] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [error, setError] = useState('');

  const fetchedBundles = useRef<BundleJson[]>();
  const fetchedOrders = useRef<PlannerOrder[]>();

  const windowGroups = useRef<WindowGroups>();
  const multiMarkers = useRef<PlannerOrder[][]>();
  const ordersFeature = useRef<PlannerOrder[][]>();
  const bundleFeature = useRef<PlannerBundle[]>();

  /* This `fetchingData` is used to prevent duplicate APIs called from `fetchData` function */
  const fetchingData = useRef(false);

  const fetchPlannerOrders = useCallback(
    async (startDate: string, endDate: string) => {
      let newOrders: Array<Order> = [];

      try {
        const response: Response = await fetch.get({
          url: `${settings.newApiUrl}/order/planner-orders?start=${startDate}&end=${endDate}&region=${region}`,
        });

        if (response.ok) {
          newOrders = await response.json();
        }
      } catch (e) {
        console.log('Error fetching the Planner Orders', e);
        setError('Error fetching the Planner Orders');
      }

      return newOrders;
    },
    [region]
  );

  const fetchPlannerBundles = useCallback(async () => {
    let newBundles: BundlesPageJson = {
      items: [],
      page: 1,
      pageSize: 1,
      total: 0,
      totalPages: 1,
    };

    try {
      const response: Response = await fetch.get({
        url: `${settings.newApiUrl}/bundle?status=${BundleStatusActive.join(
          ','
        )}&region=${region}`,
      });

      if (response.ok) {
        newBundles = await response.json();
      }
    } catch (e) {
      console.log('Error fetching the Planner Bundles', e);
      setError('Error fetching the Planner Bundles');
    }

    return newBundles;
  }, [region]);

  const fetchData = useCallback(
    async (isRefetching?: boolean, fromPusher?: boolean) => {
      fetchingData.current = true;

      /* Loading start */
      if (isRefetching) {
        /* Only show the Refetch loading when it's not update from Pusher */
        /* This is to prevent blocking the user to do things in plannermap */
        if (!fromPusher) {
          setRefetching(true);
        }
      } else {
        setLoading(true);
      }

      /* Fetch the Orders and Bundles from API */
      const formattedDate = date.format('YYYY-MM-DD');
      const ordersFromAPI = await fetchPlannerOrders(
        formattedDate,
        formattedDate
      ).catch();
      const bundlesFromAPI = await fetchPlannerBundles().catch();

      /* Process data from API */
      const bundleIds = new Set();
      const orders: PlannerOrder[] = ordersFromAPI.map(order => {
        bundleIds.add(order.bundleId);

        return {
          ...order,
          /* Start adding default value */
          senderName: order.senderName || '',
          destinationAddress: {
            fullAddressString: order.destinationAddress.fullAddressString,
            postalCode: order.destinationAddress.postalCode,
            location: order.destinationAddress.location,
          },
          product: order.product || ProductType.delivery,
          /* end of adding default value */
          /* Additional values */
          window: `${dayjs(order.deliverFrom).format('HH:mm')} - ${dayjs(
            order.deliverUntil
          ).format('HH:mm')}`,
        };
      });

      /* Sort Bundle IDs and filter the used Bundles */
      const sortedBundleIds = [...bundleIds].sort();
      const bundles = bundlesFromAPI.items.filter(({ id }) =>
        sortedBundleIds.includes(id)
      );

      /* Get Features */
      const featureStructure = createFeatureStructure(orders, bundles);

      /* Set the Data */
      /* Note : only set the Data if there is changes */
      if (JSON.stringify(fetchedBundles.current) !== JSON.stringify(bundles)) {
        fetchedBundles.current = bundles;
      }
      if (JSON.stringify(fetchedOrders.current) !== JSON.stringify(orders)) {
        fetchedOrders.current = orders;
        windowGroups.current = createGroupColors(uniqueWindowGroups(orders));
      }
      if (
        JSON.stringify(ordersFeature.current) !==
        JSON.stringify(featureStructure.ordersFeature)
      ) {
        ordersFeature.current = featureStructure.ordersFeature;
      }
      if (
        JSON.stringify(bundleFeature.current) !==
        JSON.stringify(featureStructure.bundleFeature)
      ) {
        bundleFeature.current = featureStructure.bundleFeature;
      }
      if (
        JSON.stringify(multiMarkers.current) !==
        JSON.stringify(featureStructure.multiMarkersOrderFeature)
      ) {
        multiMarkers.current = featureStructure.multiMarkersOrderFeature;
      }

      /* This `refetching` status is used for reload the component */
      if (fromPusher) {
        setRefetching(true);
      }

      /* Loading end */
      if (isRefetching) {
        /* The reFetch from Pusher still need this to make sure the component is reloading as needed */
        setRefetching(false);
      } else {
        setLoading(false);
      }

      fetchingData.current = false;
    },
    [date, fetchPlannerBundles, fetchPlannerOrders]
  );

  const reFetch = useCallback(
    async (fromPusher?: boolean) => {
      if (!fetchingData.current) {
        fetchData(true, fromPusher);
      }
    },
    [fetchData]
  );

  const reset = () => {
    fetchedBundles.current = undefined;
    fetchedOrders.current = undefined;
    windowGroups.current = undefined;
    ordersFeature.current = undefined;
    bundleFeature.current = undefined;
    multiMarkers.current = undefined;
  };

  /* This function is originally coming from the the old-planner */
  /* This is used for the websocket (PusherJS) to sync the Order data */
  const update = useCallback(
    async (orderIds: string[], event: string) => {
      /* When the bundle is modified by another user, */
      /* we should `reFetch` to make sure the current user get the updated bundle */
      if (event === bundleEventName) {
        /* Note :
         * This event will always called whenever there's changes on the bundle.
         * It means, the `reFetch` will be called multiple times
         * because of the changes done by someone else and this current user.
         */
        reFetch(true);
        return;
      }

      const newPlanner = await updatePlannerOrders(region, date, orderIds);
      let copyOfFetchedOrders: PlannerOrder[] = [
        ...(fetchedOrders.current ?? []),
      ];

      switch (event) {
        case 'order-assign-to-bundle' || 'order-updated':
          newPlanner.updatedOrders.forEach(updatedOrder => {
            const indexToUpdate = copyOfFetchedOrders.findIndex(
              currentOrder => currentOrder.id === updatedOrder.id
            );
            if (indexToUpdate !== -1) {
              copyOfFetchedOrders[indexToUpdate] = updatedOrder;
            }
          });
          break;
        case 'order-canceled':
          orderIds.forEach(orderIdToDelete => {
            const indexToUpdate = copyOfFetchedOrders.findIndex(
              currentOrder => currentOrder.id === orderIdToDelete
            );
            if (indexToUpdate !== -1) {
              copyOfFetchedOrders.splice(indexToUpdate, 1);
            }
          });
          break;
        case 'order-created':
          copyOfFetchedOrders = [
            ...(fetchedOrders.current ?? []),
            ...(newPlanner.updatedOrders ?? []),
          ];
          break;
      }
      const bundleIdsInOrder = Array.from(
        new Set(
          copyOfFetchedOrders
            .filter(({ bundleId }) => bundleId)
            .map(({ bundleId }) => bundleId)
        )
      );
      const filteredFetchedBundles = newPlanner.bundles.filter(({ id }) =>
        bundleIdsInOrder.includes(id)
      );
      const featureStructure = createFeatureStructure(
        copyOfFetchedOrders,
        filteredFetchedBundles
      );

      /* Set the Data */
      fetchedBundles.current = filteredFetchedBundles;
      fetchedOrders.current = copyOfFetchedOrders;
      windowGroups.current = createGroupColors(
        uniqueWindowGroups(copyOfFetchedOrders)
      );
      ordersFeature.current = featureStructure.ordersFeature;
      bundleFeature.current = featureStructure.bundleFeature;
      multiMarkers.current = featureStructure.multiMarkersOrderFeature;

      /* Make the plannerData updating */
      setRefetching(true);
      setRefetching(false);
    },
    [region, date, reFetch]
  );

  useSubscribeToPlannerData(update);

  useEffect(() => {
    /* Prevent re-render when it's not first initiate */
    fetchData();
  }, [fetchData]);

  return {
    loading,
    refetching,
    error,
    fetchedBundles: fetchedBundles.current,
    fetchedOrders: fetchedOrders.current,
    windowGroups: windowGroups.current,
    ordersFeature: ordersFeature.current,
    bundleFeature: bundleFeature.current,
    multiMarkers: multiMarkers.current,
    done:
      !!fetchedBundles.current &&
      !!fetchedOrders.current &&
      !!windowGroups.current &&
      !!ordersFeature.current &&
      !!bundleFeature.current &&
      !!multiMarkers.current,
    reFetch,
    reset,
    update,
  };
};
